import {
  Component,
  Input,
  OnChanges,
  OnInit,
} from "@angular/core";
import { ChartConfiguration } from "chart.js";
import {
  getBarChartData,
  getBarChartOptions,
} from "./offer-term-loan-bar-chart-data";
import { OfferCalculatorService } from "../../../../services/offer-calculator.service";
import { OfferTermCalculator } from "../../../../services/offer-term-calculator";
import { OfferActionsInternalService } from "../../../../services/actions/offer-actions.internal.service";
import { OfferBundleStatus } from "../../../../models/offer.model";
import { OfferTermLoanDetailsAdapter } from "./offer-term-details-data";
import { HorizontalListData } from "../../../../../ui/horizontal-list/horizontal-list.model";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "ifc-offer-term-details-content",
    templateUrl: "./offer-term-details-content.component.html",
    styleUrls: ["./offer-term-details-content.component.scss"],
    providers: [OfferTermLoanDetailsAdapter],
    standalone: false
})
export class OfferTermDetailsContentComponent implements OnInit, OnChanges {
  @Input()
  calculator: OfferTermCalculator;

  isRenewal: boolean;
  offerBundleStatusEnum = OfferBundleStatus;
  showSlider: boolean = false;

  private _chartData: ChartData = null;

  private _barChartData: ChartConfiguration<"bar">["data"] = null;
  private _barChartOptions: ChartConfiguration<"bar">["options"] = null;
  private _amount: number;

  detailsListItems: HorizontalListData[];

  constructor(
    private readonly actionsService: OfferActionsInternalService,
    private readonly offerCalculatorService: OfferCalculatorService,
    private readonly adapter: OfferTermLoanDetailsAdapter,
  ) {}

  ngOnInit(): void {
    this.isRenewal = !!this.calculator.application.renewalForLoanUniqueId;
    this._amount = this.calculator.amount;
    this.showSlider = this.calculator.minAmount < this.calculator.maxAmount;
    this.initListData();
    this.offerCalculatorService.inputDataChanged$.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.initListData();
      })
  }

  ngOnChanges(): void {
    this._barChartData = null;
    this._barChartOptions = null;
  }

  private initListData(): void {
    this.detailsListItems = this.adapter.adaptDetailsListData(this.calculator, this.isRenewal);
  }

  get barChartData(): any {
    if (!this._barChartData) {
      this._barChartData = this.prepareBarChartData();
    }

    return this._barChartData;
  }

  get barChartOptions(): any {
    if (!this._barChartOptions) {
      this._barChartOptions = this.prepareBarChartOptions();
    }

    return this._barChartOptions;
  }
  get amount(): number {
    return this._amount;
  }

  set amount(amount: number) {
    this._amount = amount;
    this.calculator.amount = amount;
  }

  onAmountBlur(): void {
    if (this._amount > this.calculator.maxAmount)
      this._amount = this.calculator.maxAmount;
    else if (this._amount < this.calculator.minAmount)
      this._amount = this.calculator.minAmount;

    this._barChartData = this.prepareBarChartData();
  }

  onChange(): void {
    this._barChartData = this.prepareBarChartData();
  }

  onAmountDisplay(value: number): string {
    return Math.round(value / 1000).toString() + 'K';
  }

  private get chartData(): ChartData {
    this._chartData = {
      labels: [],
      interest: [],
      principal: [],
      total: [],
    };

    const payments = this.offerCalculatorService.getTermLoanPayments(
      this.calculator.applicationOffer,
      null,
      this.calculator.amount,
    );

    for (let idx = 0; idx < payments.length; idx++) {
      this._chartData.labels.push(payments[idx].label);
      this._chartData.interest.push(payments[idx]?.interestBalance);
      this._chartData.principal.push(payments[idx]?.principalBalance);
      this._chartData.total.push(
        payments[idx]?.interestBalance + payments[idx]?.principalBalance
      );
    }

    return this._chartData;
  }

  private prepareBarChartData(): ChartConfiguration<"bar">["data"] {
    return getBarChartData(this.chartData.total, this.chartData.labels);
  }

  private prepareBarChartOptions(): ChartConfiguration<"bar">["options"] {
    return getBarChartOptions;
  }

  onLinkBank(entityId: number): void {
    this.actionsService.linkBank(entityId);
  }

  onDocumentsClick(applicationId: number, offerBundleId: number): void {
    this.actionsService.openDocumentsDetails(applicationId, offerBundleId);
  }

  showDisclosures(applicationId: number, applicationUuid: string, offerId: number): void {
    this.actionsService.showDisclosures({ applicationId, applicationUuid, offerId });
  }
}

class ChartData {
  labels: string[];
  interest: number[];
  principal: number[];
  total: number[];
}
